import React, { useState, useCallback } from "react";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Stack,
  Card,
  CardContent,
  Divider,
  Alert,
  Paper,
  alpha,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import SendIcon from "@mui/icons-material/Send";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { uploadInvoiceForChecking } from "../../../logic/ServerCalls";

const InvoiceUploader = ({
  labels,
  params,
  customerInformation,
  switchFormState,
  addExtraFiles,
  sendToCRMInvoiceUploadFailed,
}) => {
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");
  const [uploadComplete, setUploadComplete] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const allowedTypes = ["application/pdf"];
  const maxFileSize = 5 * 1024 * 1024;

  const checkInvoice = async (selectedFile) => {
    if (!selectedFile) return;

    setStatus("loading");
    setMessage("Checking your invoice...");

    try {
      const fullAddress = `${customerInformation.customerHouseNumber} ${customerInformation.customerStreet} ${customerInformation.customerZipCode} ${customerInformation.customerCity}`;

      const result = await uploadInvoiceForChecking(
        selectedFile,
        customerInformation.customerFirstName,
        customerInformation.customerLastName,
        fullAddress,
        params.lang
      );

      if (result) {
        setStatus("success");
        setMessage(
          "La facture a été chargée avec succès et correspond à la commande."
        );
        setUploadComplete(true);
      } else {
        setStatus("error");
        setMessage(
          "La facture ne correspond pas à votre commande. Une vérification manuelle sera effectuée."
        );
        setUploadComplete(true);
      }
    } catch (error) {
      setStatus("error");
      setMessage("Something went wrong. Please try again later.");
    }
  };

  const processFile = (selectedFile) => {
    if (!selectedFile) return;

    if (!allowedTypes.includes(selectedFile.type)) {
      alert(
        labels.wrong_filetype || "File type not supported. Please use PDF only."
      );
      return;
    }

    if (selectedFile.size > maxFileSize) {
      alert(labels.large_file || "File is too large. Maximum size is 5MB.");
      return;
    }

    setFile(selectedFile);
    addExtraFiles([selectedFile]);
    checkInvoice(selectedFile);
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      processFile(selectedFile);
    }
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      processFile(e.dataTransfer.files[0]);
    }
  }, []);

  const handleButtonClick = () => {
    document.getElementById("file-upload-input").click();
  };

  const renderButton = () => {
    if (status === "success") {
      return (
        <Button
          variant="contained"
          sx={{
            minWidth: "350px",
            minHeight: "45px",
            backgroundColor: "#273A60",
          }}
          onClick={() => switchFormState("payment_info")}
        >
          Continue
          <SendIcon sx={{ marginLeft: "15px" }} />
        </Button>
      );
    }

    if (status === "error") {
      return !submitted ? (
        <Button
          variant="contained"
          sx={{
            minWidth: "350px",
            minHeight: "45px",
            backgroundColor: "#273A60",
          }}
          onClick={async () => {
            setSubmitted(true);
            await sendToCRMInvoiceUploadFailed();
          }}
        >
          <ArrowForwardRoundedIcon sx={{ marginLeft: "300px" }} />
          <span className="app-span-text">{labels.submit} </span>
        </Button>
      ) : (
        <Box>
          <CircularProgress />
        </Box>
      );
    }

    return null;
  };

  return (
    <Box width="100%">
      <Card sx={{ width: "80%", boxShadow: 2, borderRadius: 1, mx: "auto" }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h5" fontWeight="500" mb={1}>
            Télécharger facture client to Télécharger justificatif de domicile
          </Typography>
          <Typography fontSize="1.2rem" fontWeight="500">
            Attention pour le bon traitement automatique par nos systèmes, il
            est important que vous chargiez le <strong>PDF</strong> original du
            justificatif de domicile.
          </Typography>

          <Divider sx={{ mb: 3 }} />

          <Stack spacing={3} alignItems="center">
            <Box
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              sx={{
                width: "100%",
                position: "relative",
                cursor: "pointer",
                ...(status !== "loading" && {
                  "&:hover": {
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05),
                  },
                }),
              }}
            >
              <input
                type="file"
                id="file-upload-input"
                accept=".pdf"
                onChange={handleFileChange}
                style={{ display: "none" }}
                disabled={status === "loading"}
              />

              <Paper
                elevation={0}
                onClick={status !== "loading" ? handleButtonClick : undefined}
                sx={{
                  border: "2px dashed",
                  borderColor: dragActive ? "primary.main" : "divider",
                  borderRadius: 2,
                  p: 4,
                  bgcolor: dragActive
                    ? (theme) => alpha(theme.palette.primary.main, 0.05)
                    : "transparent",
                  transition: "all 0.2s ease",
                  textAlign: "center",
                  cursor: status !== "loading" ? "pointer" : "default",
                }}
              >
                <CloudUploadIcon
                  sx={{
                    fontSize: 60,
                    color: dragActive ? "primary.main" : "text.secondary",
                    mb: 2,
                  }}
                />

                <Typography
                  variant="h6"
                  color={dragActive ? "primary.main" : "text.primary"}
                  gutterBottom
                >
                  {dragActive
                    ? "Drop to upload"
                    : "Chargez et déposez la facture d'électricité ou facture de téléphone (PDF)"}
                </Typography>

                <Typography variant="body2" color="text.secondary" mb={2}>
                  Formats pris en charge: PDF (Max 5MB)
                </Typography>

                {status !== "loading" && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<UploadFileIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleButtonClick();
                    }}
                  >
                    Parcourir les fichiers
                  </Button>
                )}
              </Paper>
            </Box>

            {file && status !== "loading" && (
              <Typography variant="body2" color="text.secondary">
                Sélectionné: {file.name} ({Math.round(file.size / 1024)} KB)
              </Typography>
            )}

            {status === "loading" && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  justifyContent: "center",
                  py: 1,
                }}
              >
                <CircularProgress size={24} color="primary" />
                <Typography>{message}</Typography>
              </Box>
            )}

            {status === "success" && (
              <Alert
                icon={<CheckCircleIcon fontSize="inherit" />}
                severity="success"
                sx={{ width: "100%" }}
              >
                {message}
              </Alert>
            )}

            {status === "error" && (
              <Paper
                elevation={2}
                sx={{
                  p: 2,
                  bgcolor: "#FFF4E5",
                  borderRadius: 1,
                  width: "100%",
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <WarningAmberIcon color="warning" />
                  <Typography color="text.secondary">{message}</Typography>
                </Stack>
              </Paper>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
        {renderButton()}
      </Box>
    </Box>
  );
};

export default InvoiceUploader;
